import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVehicleListApi } from '@hero/api-hooks';
import { useFeature } from '@hero/web-context';
import { TruckIcon } from '@hero/web-icons';
import { VehicleTable } from './VehicleTable';
import { ButtonCreateEntity } from '../../../app/ButtonCreateEntity';
import { TableEmptyState } from '../../../app/Table';
import { VehicleEditModal } from '../Modal/VehicleEditModal';

export interface VehicleTableManagerProps {
  onCreateClick?: () => void;
  hideCreateButton?: boolean;
}

export function VehicleTableManager({ onCreateClick, hideCreateButton }: VehicleTableManagerProps) {
  const { t } = useTranslation(['m-vehicle']);
  const { isEnabled: canCreateVehicle } = useFeature('vehicle_module', 'sub_create_vehicle');

  const {
    data: vehicles,
    isLoading,
    onLoadMore,
    hasNextPage,
    isFetchingNextPage,
    runQuery,
  } = useVehicleListApi({});

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      runQuery({ search: term || undefined });
    },
    [runQuery],
  );

  const handleStatusFilterChange = useCallback(
    (value: string | undefined) => {
      const next = value === statusFilter ? undefined : value;
      setStatusFilter(next);
      runQuery({ status: next });
    },
    [runQuery, statusFilter],
  );

  const handleSort = useCallback(
    (column: string) => {
      const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
      setSortColumn(column);
    },
    [sortColumn, sortDirection],
  );

  const stats = useMemo(() => {
    const counts: Record<string, number> = { active: 0, busy: 0, no_driver: 0 };
    for (const v of vehicles) {
      if (v.status === 'active') counts['active']++;
      if (v.status === 'busy') counts['busy']++;
      if (!v.driver) counts['no_driver']++;
    }
    return counts;
  }, [vehicles]);

  const statusFilterOptions = useMemo(
    () => [
      {
        value: 'active',
        label: t('status.active'),
        className: 'bg-success-50 text-success-400',
      },
      {
        value: 'busy',
        label: t('status.busy'),
        className: 'bg-warning-100 text-warning-400',
      },
      {
        value: 'no_driver',
        label: t('status.no_driver'),
        className: 'bg-gray-100 text-gray-600',
      },
    ],
    [t],
  );

  const statusFilterCounts = useMemo(
    () => [
      { value: 'active', count: stats['active'] },
      { value: 'busy', count: stats['busy'] },
      { value: 'no_driver', count: stats['no_driver'] },
    ],
    [stats],
  );

  const handleCreateClick = () => {
    if (onCreateClick) {
      onCreateClick();
    } else {
      setIsCreateOpen(true);
    }
  };

  const emptyState = (
    <TableEmptyState
      icon={<TruckIcon className="h-7 w-7" />}
      title={t('m-vehicle:table.empty.title')}
      description={t('m-vehicle:table.empty.description')}
      action={
        !hideCreateButton && canCreateVehicle ? (
          <ButtonCreateEntity
            label={t('m-vehicle:table.empty.cta')}
            onClick={handleCreateClick}
          />
        ) : undefined
      }
    />
  );

  return (
    <>
      <VehicleTable
        vehicles={vehicles}
        isLoading={isLoading}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        searchProps={{ term: searchTerm, setTerm: handleSearch }}
        onSort={handleSort}
        onLoadMore={onLoadMore}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        emptyState={emptyState}
        hasActiveFilters={Boolean(searchTerm) || Boolean(statusFilter)}>
        <VehicleTable.Head>
          <VehicleTable.Search />
          <div className="flex items-center gap-3">
            <VehicleTable.Badge
              options={statusFilterOptions}
              counts={statusFilterCounts}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              showCounts={false}
            />
            {!hideCreateButton && canCreateVehicle && (
              <ButtonCreateEntity
                label={t('modal.create_vehicle_title')}
                onClick={handleCreateClick}
              />
            )}
          </div>
        </VehicleTable.Head>
      </VehicleTable>

      <VehicleEditModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
    </>
  );
}
